// import React, { createContext, useState, useEffect } from "react";
// import { jwtDecode } from "jwt-decode";

// export const PartnerContext = createContext();

// export const PartnerProvider = ({ children }) => {
//   const [isLoggedIn, setIsLoggedIn] = useState(false);
//   const [partner, setPartner] = useState(null);
//   const [loading, setLoading] = useState(true);

//   const loadUserFromToken = () => {
//     const token = localStorage.getItem("partnerToken");

//     if (!token) {
//       setIsLoggedIn(false);
//       setPartner(null);
//       setLoading(false);
//       return;
//     }

//     try {
//       const decoded = jwtDecode(token);

//       setPartner({
//         id: decoded.id,
//         fname: decoded.fname,
//         email: decoded.email,
//         role: decoded.role,
//       });
//       setIsLoggedIn(true);
//     } catch (err) {
//       localStorage.removeItem("partnerToken");
//       setIsLoggedIn(false);
//       setPartner(null);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     loadUserFromToken();
//   }, []);
//   useEffect(() => {
//     console.log("Partner state:", partner);
//   }, [partner]);

//   const login = (token) => {
//     localStorage.setItem("role", "partner");
//     localStorage.setItem("partnerToken", token);
//     loadUserFromToken();
//   };

//   const logout = () => {
//     localStorage.removeItem("partnerToken");
//     localStorage.removeItem("role");
//     setIsLoggedIn(false);
//     setPartner(null);
//   };

//   return (
//     <PartnerContext.Provider value={{ isLoggedIn, partner, login, logout, loading }}>
//       {children}
//     </PartnerContext.Provider>
//   );
// };

// export const usePartner = () => React.useContext(PartnerContext);

import React, { createContext, useState, useEffect, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import { getPartnerProfileStatus } from "../services/partner/profile";

export const PartnerContext = createContext();

export const PartnerProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);

  // Notifications state lives here too
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Mirrors AuthContext's approvalPending for childcare — here it tracks
  // profile completion (partner_registrations.is_profile_complete) rather
  // than admin approval, but drives the same Dashboard gating pattern.
  // Fetched once per login here in context, not from the Dashboard itself.
  const [approvalPending, setApprovalPending] = useState(false);
  // True until the first registration-status check resolves (or is skipped
  // because there's no logged-in partner). Consumers must wait for this to
  // go false before trusting approvalPending — otherwise its default value
  // (false) can let a page fire a real-data fetch before we actually know
  // registration is incomplete, surfacing a raw API error instead of guidance.
  const [approvalPendingLoading, setApprovalPendingLoading] = useState(true);
  const [adminVerified, setAdminVerified] = useState(false);

  // Message unread count + recent conversations for navbar badge
  const [msgUnreadCount, setMsgUnreadCount] = useState(0);
  const [recentConversations, setRecentConversations] = useState([]);

  const socketRef = useRef(null);

  const loadUserFromToken = () => {
    const token = localStorage.getItem("partnerToken") || sessionStorage.getItem("partnerToken");

    if (!token) {
      setIsLoggedIn(false);
      setPartner(null);
      setLoading(false);
      return;
    }

    try {
      const decoded = jwtDecode(token);
      setPartner({
        id: decoded.id,
        fname: decoded.fname,
        email: decoded.email,
        role: decoded.role,
      });
      setIsLoggedIn(true);
    } catch (err) {
      console.error("Failed to decode partner token:", err);
      setIsLoggedIn(false);
      setPartner(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUserFromToken();
  }, []);

  // Fetch profile-completion status once per login — cached in context so
  // the Dashboard never needs to call this itself.
  const refreshApprovalPending = async () => {
    setApprovalPendingLoading(true);
    try {
      const res = await getPartnerProfileStatus();
      setApprovalPending(!res.data?.data?.isComplete);
      setAdminVerified(!!res.data?.data?.adminVerified);
    } catch (_) {
      // Fail-open: don't block the dashboard if the status check errors out.
      setApprovalPending(false);
      setAdminVerified(false);
    } finally {
      setApprovalPendingLoading(false);
    }
  };

  useEffect(() => {
    // Wait for token decoding (the `loading` flag above) to settle first —
    // otherwise this can run once with a stale `partner === null` closure
    // and flip approvalPendingLoading to false a tick before `partner` is
    // actually set, letting consumers fetch real data too early.
    if (loading) return;
    if (!partner) {
      setApprovalPending(false);
      setApprovalPendingLoading(false);
      return;
    }
    refreshApprovalPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner?.id, loading]);

  // Connect socket whenever partner logs in, disconnect on logout
  useEffect(() => {
    if (!partner) return;

    const token = localStorage.getItem("partnerToken") || sessionStorage.getItem("partnerToken");
    if (!token) return;

    // Fetch existing notifications and conversations from REST first
    fetchNotifications(token);
    fetchConversations(token);

    // Connect socket — loaded on demand so anonymous visitors never pull
    // socket.io-client into the main bundle.
    let cancelled = false;
    import("socket.io-client").then(({ io }) => {
      if (cancelled) return;

      const socket = io(process.env.REACT_APP_SOCKET_URL, {
        auth: { token },
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 2000,
      });

      socket.on("connect", () => {
        console.log("🔔 Notification socket connected");
      });

      socket.on("notification:new", (notif) => {
        try {
          const saved = localStorage.getItem("partner_notif_prefs");
          const prefs = saved ? JSON.parse(saved) : {};
          if (prefs.push === false) return;
          const enrollmentTypes = ["new_parent_registration", "new_partner_registration"];
          const reminderTypes = ["reminder", "event_reminder", "class_reminder"];
          if (enrollmentTypes.includes(notif.type) && prefs.enrollments === false) return;
          if (reminderTypes.includes(notif.type) && prefs.reminders === false) return;
        } catch {}
        setNotifications((prev) => [notif, ...prev]);
        setUnreadCount((prev) => prev + 1);
      });


      socket.on("connect_error", (err) => {
        console.error("Socket error:", err.message);
      });

      socketRef.current = socket;
    });

    // Cleanup on logout or unmount
    return () => {
      cancelled = true;
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [partner]); // re-runs when partner changes (login/logout)

  const fetchNotifications = async (token) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/partner/notifications`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.success) {
        setNotifications(data.notifications);
        setUnreadCount(data.unreadCount);
      }
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    }
  };

  const fetchConversations = async (token) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/partner/messages/conversations`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      const convs = data.conversations || data.data || [];
      const totalUnread = convs.reduce((sum, c) => sum + (c.unread_count || 0), 0);
      setMsgUnreadCount(totalUnread);
      setRecentConversations(convs.slice(0, 10));
    } catch (err) {
      console.error("Failed to fetch conversations:", err);
    }
  };

  const clearMsgUnread = () => setMsgUnreadCount(0);

  const pushNotification = (notif) => {
    setNotifications((prev) => [notif, ...prev]);
    setUnreadCount((prev) => prev + 1);
  };

  const markAllRead = async () => {
    const token = localStorage.getItem("partnerToken") || sessionStorage.getItem("partnerToken");
    try {
      await fetch(
        // `${process.env.REACT_APP_API_BASE_URL}/api/partner/notifications/mark-read`,
        `${process.env.REACT_APP_BACKEND_URL}/partner/notifications/mark-read`,
        { method: "PATCH", headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all read:", err);
    }
  };

  const markOneRead = async (id) => {
    const token = localStorage.getItem("partnerToken") || sessionStorage.getItem("partnerToken");
    try {
      await fetch(
        // `${process.env.REACT_APP_API_BASE_URL}/api/partner/notifications/${id}/read`,
        `${process.env.REACT_APP_BACKEND_URL}/partner/notifications/${id}/read`,
        { method: "PATCH", headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Failed to mark read:", err);
    }
  };

  const login = (token, rememberMe = true) => {
    try {
      const decoded = jwtDecode(token);
      localStorage.setItem("role", decoded.role || "partner");
    } catch {
      localStorage.setItem("role", "partner");
    }
    if (rememberMe) {
      localStorage.setItem("partnerToken", token);
      sessionStorage.removeItem("partnerToken");
    } else {
      sessionStorage.setItem("partnerToken", token);
      localStorage.removeItem("partnerToken");
    }
    loadUserFromToken();
    window.dispatchEvent(new Event("userChanged"));
  };

  const logout = () => {
    localStorage.removeItem("partnerToken");
    sessionStorage.removeItem("partnerToken");
    localStorage.removeItem("role");
    setIsLoggedIn(false);
    setPartner(null);
    setNotifications([]);
    setUnreadCount(0);
    setMsgUnreadCount(0);
    setRecentConversations([]);
    // socket cleanup is handled by the useEffect return above
  };

  return (
    <PartnerContext.Provider
      value={{
        isLoggedIn,
        partner,
        login,
        logout,
        loading,
        notifications,
        unreadCount,
        markAllRead,
        markOneRead,
        msgUnreadCount,
        recentConversations,
        clearMsgUnread,
        pushNotification,
        approvalPending,
        approvalPendingLoading,
        refreshApprovalPending,
        adminVerified,
      }}
    >
      {children}
    </PartnerContext.Provider>
  );
};

export const usePartner = () => React.useContext(PartnerContext);